import { EmailParameterApi, emailparametersApi } from "api";
import React, { useState } from "react";
import { useToaster } from "utils";
import { CreateParameterForm, CreateParameterFormValues } from "./CreateParameterForm";
import { EditParameterForm, EditParameterFormValues } from "./EditParameterForm";
import { PageHeader, PageMain } from "components/molecules";
import { PaziaBox, PaziaBreadcrumbs } from "components/atoms";
import { routes } from "consts";
import { useEmailParameters } from "api/hooks";
import { PaziaInlineLoader } from "components/atoms";
import { EmailPageHeader } from "../components/EmailPageHeader";

export const EmailTemplateParametersPage = (): JSX.Element => {
  const { isFetching, parameters, invalidate } = useEmailParameters(0, 1_000_000);
  const { showSuccess, showError } = useToaster();
  const [selectedParam, setSelectedParam] = useState<EmailParameterApi | null | {}>(null);

  const onAdd = async (data: CreateParameterFormValues) => {
    try {
      await emailparametersApi.create(data);
      showSuccess(`Utworzono parametr ${data.name}`);
      setSelectedParam(null);
    } catch (e) {
      console.error(e);
      showError("Wystąpił błąd podczas tworzenia parametru");
    } finally {
      invalidate();
    }
  };

  const onEdit = async (emailParameterId: number, data: EditParameterFormValues) => {
    try {
      await emailparametersApi.update(emailParameterId, data);
      showSuccess(`Zmodyfikowano parametr ${data.name}`);
      setSelectedParam(null);
    } catch (e) {
      console.error(e);
      showError("Wystąpił błąd podczas modyfikowania parametru");
    } finally {
      invalidate();
    }
  };

  const onDelete = async (emailParameterId: number, paramName: string) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć parametr "${paramName}"?`)) {
      return;
    }
    try {
      await emailparametersApi.delete(emailParameterId);
      showSuccess(`Usunięto parametr ${paramName}`);
      setSelectedParam(null);
    } catch (e) {
      console.error(e);
      showError("Wystąpił błąd podczas usuwania parametru");
    } finally {
      invalidate();
    }
  };

  // TODO Loading icon
  return (
    <div>
      <EmailPageHeader>Parametry</EmailPageHeader>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <PaziaBox header="Parametry">
          <div className="divide-y overflow-auto">
            <ParameterRow onClick={() => setSelectedParam({})}>Dodaj nowy...</ParameterRow>
            {isFetching ? (
              <div className="flex justify-center">
                <div className="py-4">
                  <PaziaInlineLoader />
                </div>
              </div>
            ) : (
              parameters.map((par) => (
                <ParameterRow key={par.name} onClick={() => setSelectedParam(par)}>
                  <span className="pl-2">{par.name}</span>
                </ParameterRow>
              ))
            )}
          </div>
        </PaziaBox>
        <PaziaBox className="md:col-span-2">
          {selectedParam !== null ? (
            <ParameterForm parameter={selectedParam} onAdd={onAdd} onEdit={onEdit} onDelete={onDelete} />
          ) : (
            <div className="flex justify-center">Wybierz parametr po lewej stronie</div>
          )}
        </PaziaBox>
      </div>
    </div>
  );
};

interface ParameterFormProps {
  parameter?: {} | EmailParameterApi;
  onAdd: (data: CreateParameterFormValues) => void;
  onEdit: (emailParameterId: number, data: EditParameterFormValues) => void;
  onDelete: (emailParameterId: number, paramName: string) => void;
}

const ParameterForm = ({ parameter, onAdd, onEdit, onDelete }: ParameterFormProps) => {
  if (!isEmailParameterApi(parameter)) return <CreateParameterForm onSubmit={onAdd} />;
  return (
    <EditParameterForm
      parameter={parameter}
      onSubmit={(x) => onEdit(parameter.id, x)}
      onDelete={() => onDelete(parameter.id, parameter.name)}
    />
  );
};

interface ParameterRowProps {
  children: React.ReactNode;
  onClick?: () => void;
}

const ParameterRow = ({ children, onClick }: ParameterRowProps) => (
  <div
    className="hover:bg-primary-50 dark:hover:bg-primary-700 cursor-pointer p-2 dark:hover:text-black"
    onClick={onClick}
  >
    {children}
  </div>
);

function isEmailParameterApi(obj: any): obj is EmailParameterApi {
  return typeof obj?.name === "string";
}
